import { createFileRoute } from '@tanstack/react-router'
import { RouteErrorFallback } from '@/components/ErrorBoundary'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Card, CardContent } from '@/components/ui/card'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'
import { Ticket, PhoneCall, CheckCircle2, XCircle } from 'lucide-react'
import { useState, useMemo, useEffect } from 'react'
import { DataView, DataViewToolbar, type DataViewColumn, type DataViewAction, type ViewMode } from '@/components/data-view'
import { useLocalStorage } from '@/hooks/useLocalStorage'
import { useCallbacksPage, useCallbacksSearchPage, useCallback } from '@/hooks/useCallbacks'
import { useCallbackMutations } from '@/hooks/useCallbackMutations'
import { useDebounce } from '@/hooks/useDebounce'
import { CallbackState, type CallbackResponseDTO } from '@/api/generated/models'
import { formatRelativeDateTime, formatUTCToLocalDateTime } from '@/lib/dateUtils'
import { TicketDialog } from '@/components/TicketDialog'
import { cn } from '@/lib/utils'
import { TablePagination } from '@/components/ui/table-pagination'
import {
  PageContainer,
  PageCard,
  PageHeader,
  PageHeaderContent,
  PageHeaderTitle,
  PageHeaderHeading,
  PageHeaderDescription,
  PageContent,
} from '@/components/ui/page'

const PAGE_SIZE_OPTIONS = [10, 20, 50, 100]

export const Route = createFileRoute('/callbacks')({
  component: Callbacks,
  errorComponent: RouteErrorFallback,
  validateSearch: (search: Record<string, unknown>) => ({
    id: (search.id as string) || undefined,
  }),
})

type StateFilter = 'all' | CallbackState

interface TicketRow {
  id: string
  name: string
  phoneNumber?: string | null
  intention: string
  state: CallbackState
  createdAt: string
  createdAtExact: string
}

function getStateLabel(state: CallbackState): string {
  switch (state) {
    case CallbackState.Pending:
      return 'Ausstehend'
    case CallbackState.Tried:
      return 'Versucht'
    case CallbackState.Completed:
      return 'Erledigt'
    case CallbackState.Failed:
      return 'Fehlgeschlagen'
    default:
      return state
  }
}

function StateBadge({ state }: { state: CallbackState }) {
  return (
    <span
      className={cn(
        'inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold',
        state === CallbackState.Pending && 'bg-yellow-100 text-yellow-800',
        state === CallbackState.Tried && 'bg-blue-100 text-blue-800',
        state === CallbackState.Completed && 'bg-green-100 text-green-800',
        state === CallbackState.Failed && 'bg-red-100 text-red-800'
      )}
    >
      {getStateLabel(state)}
    </span>
  )
}

function Callbacks() {
  const { id } = Route.useSearch()

  const [view, setView] = useLocalStorage<ViewMode>('audavo:view:callbacks', 'table')
  const [searchTerm, setSearchTerm] = useState('')
  const [stateFilter, setStateFilter] = useState<StateFilter>('all')
  const [selectedCallbackId, setSelectedCallbackId] = useState<string | null>(null)
  const [isDialogOpen, setIsDialogOpen] = useState(false)

  useEffect(() => {
    if (id) {
      setSelectedCallbackId(id)
      setIsDialogOpen(true)
    }
  }, [id])

  const debouncedSearch = useDebounce(searchTerm.trim(), 300)
  const isSearching = debouncedSearch.length > 0

  const apiState = stateFilter === 'all' ? null : (stateFilter as CallbackState)

  const [page, setPage] = useState(0)
  const [pageSize, setPageSize] = useState(() =>
    typeof window !== 'undefined' && window.innerHeight >= 900 ? 50 : 20
  )

  useEffect(() => {
    setPage(0)
  }, [debouncedSearch, stateFilter, pageSize])

  const listQuery = useCallbacksPage({
    page,
    pageSize,
    state: apiState,
    enabled: !isSearching,
  })

  const searchQuery = useCallbacksSearchPage({
    page,
    pageSize,
    q: debouncedSearch,
    state: apiState,
    enabled: isSearching,
  })

  const {
    data,
    isLoading,
    error,
  } = isSearching ? searchQuery : listQuery

  const allCallbacks = useMemo((): CallbackResponseDTO[] => {
    return data?.results ?? []
  }, [data])

  const total = data?.total ?? 0

  const { updateCallbackState } = useCallbackMutations()

  // Deep-linked ticket may not be on the current page — fetch it individually as fallback.
  const isSelectedOnPage = allCallbacks.some((c) => c.id === selectedCallbackId)
  const { data: fetchedCallback } = useCallback(
    !isSelectedOnPage && selectedCallbackId ? selectedCallbackId : ''
  )

  const selectedCallback = useMemo(() => {
    if (!selectedCallbackId) return null
    return allCallbacks.find((c) => c.id === selectedCallbackId) ?? fetchedCallback ?? null
  }, [selectedCallbackId, allCallbacks, fetchedCallback])

  const rows = useMemo((): TicketRow[] => {
    return allCallbacks.map((callback) => ({
      id: callback.id,
      name: callback.name,
      phoneNumber: callback.phone_number,
      intention: callback.intention,
      state: callback.state,
      createdAt: formatRelativeDateTime(callback.created_at),
      createdAtExact: formatUTCToLocalDateTime(callback.created_at),
    }))
  }, [allCallbacks])

  const handleRowClick = (row: TicketRow) => {
    setSelectedCallbackId(row.id)
    setIsDialogOpen(true)
  }

  const columns: DataViewColumn<TicketRow>[] = [
    {
      id: 'name',
      header: 'Name',
      className: 'font-medium',
      cell: (row) => row.name,
    },
    {
      id: 'phoneNumber',
      header: 'Telefonnummer',
      width: '180px',
      cell: (row) => row.phoneNumber,
    },
    {
      id: 'intention',
      header: 'Anliegen',
      cell: (row) => (
        <Tooltip>
          <TooltipTrigger asChild>
            <div className="max-w-xs truncate cursor-help">{row.intention}</div>
          </TooltipTrigger>
          <TooltipContent>
            <p className="max-w-sm">{row.intention}</p>
          </TooltipContent>
        </Tooltip>
      ),
    },
    {
      id: 'state',
      header: 'Status',
      width: '160px',
      cell: (row) => <StateBadge state={row.state} />,
    },
    {
      id: 'createdAt',
      header: 'Zeitpunkt',
      width: '200px',
      cell: (row) => (
        <Tooltip>
          <TooltipTrigger asChild>
            <span className="cursor-default">{row.createdAt}</span>
          </TooltipTrigger>
          <TooltipContent>
            <p>{row.createdAtExact}</p>
          </TooltipContent>
        </Tooltip>
      ),
    },
  ]

  const actions: DataViewAction<TicketRow>[] = [
    {
      id: 'tried',
      label: 'Versucht',
      icon: PhoneCall,
      hidden: (row) => row.state !== CallbackState.Pending,
      onSelect: (row) => updateCallbackState.mutate({ callbackId: row.id, state: CallbackState.Tried }),
    },
    {
      id: 'completed',
      label: 'Erledigt',
      icon: CheckCircle2,
      hidden: (row) => row.state !== CallbackState.Pending && row.state !== CallbackState.Tried,
      onSelect: (row) => updateCallbackState.mutate({ callbackId: row.id, state: CallbackState.Completed }),
    },
    {
      id: 'failed',
      label: 'Fehlgeschlagen',
      icon: XCircle,
      variant: 'destructive',
      hidden: (row) => row.state !== CallbackState.Tried,
      onSelect: (row) => updateCallbackState.mutate({ callbackId: row.id, state: CallbackState.Failed }),
    },
  ]

  const paginationControl = (
    <TablePagination
      currentPage={page + 1}
      totalPages={Math.max(1, Math.ceil(total / pageSize))}
      totalItems={total}
      pageSize={pageSize}
      onPageChange={(p) => setPage(p - 1)}
      onPageSizeChange={setPageSize}
      pageSizeOptions={PAGE_SIZE_OPTIONS}
    />
  )

  const showPagination = !isLoading && !error && (rows.length > 0 || page > 0)

  const renderCard = (row: TicketRow) => (
    <Card>
      {/* Rechts halten wir auf Touch Platz fuer das Ecken-Aktionsmenue frei. */}
      <CardContent className="flex items-center gap-4 p-3 pointer-coarse:pr-12">
        <span className="min-w-0 flex-1 truncate font-medium">{row.name}</span>
        <span className="hidden shrink-0 text-sm text-muted-foreground truncate max-w-[16rem] sm:inline">{row.intention}</span>
        {/* In der Tabelle steht der exakte Zeitpunkt nur im Tooltip — auf Touch
            gibt es keinen, deshalb hier direkt. */}
        <span className="shrink-0 text-sm text-muted-foreground">{row.createdAtExact}</span>
        <div className="shrink-0">
          <StateBadge state={row.state} />
        </div>
      </CardContent>
    </Card>
  )

  const statusFilter = (
    <Select value={stateFilter} onValueChange={(value) => setStateFilter(value as StateFilter)}>
      <SelectTrigger className="w-full sm:w-[200px]">
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value="all">Alle Tickets</SelectItem>
        <SelectItem value={CallbackState.Pending}>Ausstehend</SelectItem>
        <SelectItem value={CallbackState.Tried}>Versucht</SelectItem>
        <SelectItem value={CallbackState.Completed}>Erledigt</SelectItem>
        <SelectItem value={CallbackState.Failed}>Fehlgeschlagen</SelectItem>
      </SelectContent>
    </Select>
  )

  return (
    <PageContainer>
      <PageCard>
        <PageHeader>
          <PageHeaderContent>
            <PageHeaderTitle hiddenOnMobile>
              <PageHeaderHeading>Ticketliste</PageHeaderHeading>
              <PageHeaderDescription>Verwalten Sie Ticketanfragen und verfolgen Sie deren Status</PageHeaderDescription>
            </PageHeaderTitle>
            <DataViewToolbar
              view={view}
              onViewChange={setView}
              search={{
                value: searchTerm,
                onChange: setSearchTerm,
                placeholder: 'Name, Telefon, Anliegen…',
              }}
              filters={statusFilter}
            />
          </PageHeaderContent>
        </PageHeader>

        <PageContent className="p-6">
          <TooltipProvider>
            <DataView
              items={rows}
              getRowId={(row) => row.id}
              columns={columns}
              renderCard={renderCard}
              actions={actions}
              onItemClick={handleRowClick}
              storageKey="audavo:view:callbacks"
              cardGridClassName="grid-cols-1 gap-3 md:grid-cols-1 lg:grid-cols-1"
              view={view}
              onViewChange={setView}
              isLoading={isLoading}
              error={error}
              emptyState={
                isSearching
                  ? {
                      icon: Ticket,
                      title: 'Keine Treffer',
                      description: `Für „${debouncedSearch}“ wurden keine Tickets gefunden.`,
                    }
                  : {
                      icon: Ticket,
                      title: 'Keine Tickets',
                      description: 'Es wurden noch keine Tickets erfasst.',
                    }
              }
              pagination={showPagination ? paginationControl : undefined}
            />
          </TooltipProvider>
        </PageContent>
      </PageCard>

      <TicketDialog
        callback={selectedCallback}
        open={isDialogOpen}
        onOpenChange={setIsDialogOpen}
      />
    </PageContainer>
  )
}
